<script lang="js">
import axios from "axios";
import { RouterLink } from "vue-router";

export default {
  name: "StaffReports",
  data() {
    return {
      logger: null,
      loading: false,
      currentSession: null,
      dashboardStats: {
        applications: 0,
        enrolledStudents: 0,
        totalRevenue: 0,
        totalUsers: 0,
      },
      statusCounts: {
        draft: 0,
        submitted: 0,
        admission_offered: 0,
        rejected: 0,
        withdrawn: 0,
        enrolled: 0,
      },
      generatedAt: new Date(),
    };
  },
  computed: {
    totalStatusCount() {
      return Object.values(this.statusCounts).reduce((sum, count) => sum + count, 0);
    },
    formattedRevenue() {
      return `N${Number(this.dashboardStats.totalRevenue || 0).toLocaleString("en-NG")}`;
    },
  },
  created() {
    this.logger = this.$createLogger("staffReports");
  },
  async mounted() {
    await this.fetchReportData();
  },
  methods: {
    async fetchReportData() {
      try {
        this.loading = true;
        await Promise.all([this.fetchDashboardStats(), this.fetchStatusCounts()]);
        this.generatedAt = new Date();
      } catch (error) {
        this.logger.error("Error loading reports:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchDashboardStats() {
      const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/staff/dashboard-stats`);

      if (response.data?.success) {
        this.currentSession = response.data.data.currentSession;
        this.dashboardStats = {
          applications: response.data.data.stats?.applications || 0,
          enrolledStudents: response.data.data.stats?.enrolledStudents || 0,
          totalRevenue: response.data.data.stats?.totalRevenue || 0,
          totalUsers: response.data.data.stats?.totalUsers || 0,
        };
      }
    },
    async fetchStatusCounts() {
      const statuses = ["draft", "submitted", "admission_offered", "rejected", "withdrawn", "enrolled"];
      const requests = statuses.map((status) =>
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/applications`, {
          params: {
            page: 1,
            limit: 1,
            status,
            sortBy: "createdAt",
            sortOrder: "desc",
          },
        })
      );

      const results = await Promise.all(requests);
      const counts = {};

      statuses.forEach((status, index) => {
        counts[status] = results[index].data?.data?.pagination?.total || 0;
      });

      this.statusCounts = counts;
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case "draft":
        case "submitted":
          return "warning";
        case "admission_offered":
        case "enrolled":
          return "success";
        case "rejected":
          return "danger";
        case "withdrawn":
          return "secondary";
        default:
          return "dark";
      }
    },
    getStatusLabel(status) {
      const labels = {
        draft: "Draft",
        submitted: "Submitted",
        admission_offered: "Admission Offered",
        rejected: "Rejected",
        withdrawn: "Withdrawn",
        enrolled: "Enrolled",
      };
      return labels[status] || status;
    },
    percentage(value) {
      if (!this.totalStatusCount) {
        return 0;
      }
      return Math.round((value / this.totalStatusCount) * 100);
    },
  },
  components: { RouterLink },
};
</script>

<template>
  <div class="col-lg-12 col-md-12">
    <div class="p-4 text-start">
      <div
        class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-3"
      >
        <div>
          <h4 class="fw-bold mb-1">Reports</h4>
          <p class="text-muted mb-0">
            Session: {{ currentSession?.sessionYear || "N/A" }} · Generated:
            {{ generatedAt.toLocaleString() }}
          </p>
        </div>
        <button
          class="btn btn-sm btn-outline-primary"
          @click="fetchReportData"
          :disabled="loading"
        >
          <i class="bi bi-arrow-clockwise me-1" :class="{ spin: loading }"></i>
          Refresh Report
        </button>
      </div>

      <hr class="mb-4" />

      <div class="row g-3 mb-4">
        <div class="col-md-6 col-lg-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <small class="text-muted">Applications</small>
              <h4 class="mb-0">{{ dashboardStats.applications }}</h4>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <small class="text-muted">Enrolled Students</small>
              <h4 class="mb-0">{{ dashboardStats.enrolledStudents }}</h4>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <small class="text-muted">Total Revenue</small>
              <h4 class="mb-0">{{ formattedRevenue }}</h4>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <small class="text-muted">Total Users</small>
              <h4 class="mb-0">{{ dashboardStats.totalUsers }}</h4>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-4">
        <div class="col-lg-8">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white">
              <h6 class="mb-0">Application Status Breakdown</h6>
            </div>
            <div class="card-body">
              <div
                v-for="(count, status) in statusCounts"
                :key="status"
                class="mb-3"
              >
                <div
                  class="d-flex justify-content-between align-items-center mb-1"
                >
                  <span
                    class="badge"
                    :class="`text-bg-${getStatusBadgeClass(status)}`"
                    >{{ getStatusLabel(status) }}</span
                  >
                  <span class="small text-muted"
                    >{{ count }} ({{ percentage(count) }}%)</span
                  >
                </div>
                <div class="progress" style="height: 8px">
                  <div
                    class="progress-bar"
                    :class="`bg-${getStatusBadgeClass(status)}`"
                    role="progressbar"
                    :style="`width: ${percentage(count)}%`"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-header bg-white">
              <h6 class="mb-0">Report Shortcuts</h6>
            </div>
            <div class="card-body d-grid gap-2">
              <RouterLink
                class="btn btn-dark"
                :to="{ name: 'applicationsMgt' }"
              >
                <i class="bi bi-file-earmark-text me-1"></i> Applications
              </RouterLink>
              <RouterLink
                class="btn btn-outline-dark"
                :to="{ name: 'studentsMgt' }"
              >
                <i class="bi bi-people me-1"></i> Students
              </RouterLink>
              <RouterLink
                class="btn btn-outline-dark"
                :to="{ name: 'paymentsMgt' }"
              >
                <i class="bi bi-cash-coin me-1"></i> Payments
              </RouterLink>
              <RouterLink
                class="btn btn-outline-dark"
                :to="{ name: 'staffSettings' }"
              >
                <i class="bi bi-gear me-1"></i> Settings
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.spin {
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
